import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'motion/react'
import { Gem } from 'lucide-react'

// Множители выигрыша по цветам
const COLOR_MULTIPLIER = { red: 2, black: 2, green: 14 }
// Длительность анимации счётчика (мс)
const COUNTER_DURATION = 1000

// Анимированный счётчик суммы: плавно меняет число от startValue к targetValue за COUNTER_DURATION мс
const AnimatedSum = ({ value, isWinner, isLoser, isAnimating }) => {
  const [displayValue, setDisplayValue] = useState(value)
  const rafRef = useRef(null)
  const startTsRef = useRef(null)
  const startValRef = useRef(value)

  useEffect(() => {
    if (!isAnimating) {
      // Сброс — показываем реальное значение
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      setDisplayValue(value)
      return
    }

    const from = value
    const to = isWinner ? value : 0
    startValRef.current = from
    startTsRef.current = null

    const tick = (ts) => {
      if (!startTsRef.current) startTsRef.current = ts
      const elapsed = ts - startTsRef.current
      const progress = Math.min(elapsed / COUNTER_DURATION, 1)
      // easeOutQuad для плавного замедления
      const eased = 1 - (1 - progress) * (1 - progress)
      const current = Math.round(from + (to - from) * eased)
      setDisplayValue(current)

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [isAnimating, isWinner, isLoser, value])

  // Для победителей после анимации уменьшения — показываем оригинал, умножение отображается отдельно
  return <>{Math.max(0, displayValue).toLocaleString()}</>
}

// Анимированный множитель суммы для победителей: value → value * multiplier
const AnimatedWinSum = ({ value, multiplier, isAnimating }) => {
  const target = value * multiplier
  const [displayValue, setDisplayValue] = useState(value)
  const rafRef = useRef(null)
  const startTsRef = useRef(null)

  useEffect(() => {
    if (!isAnimating) {
      setDisplayValue(value)
      return
    }

    startTsRef.current = null

    const tick = (ts) => {
      if (!startTsRef.current) startTsRef.current = ts
      const elapsed = ts - startTsRef.current
      const progress = Math.min(elapsed / COUNTER_DURATION, 1)
      const eased = 1 - (1 - progress) * (1 - progress)
      const current = Math.round(value + (target - value) * eased)
      setDisplayValue(current)

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }
  }, [isAnimating, value, target])

  return <>{displayValue.toLocaleString()}</>
}

const getBetKey = (bet, columnKey, index) => {
  const stableId = bet?.id ?? bet?.bet_id ?? bet?.created_at ?? null

  if (stableId !== null && stableId !== undefined && stableId !== '') {
    return `${columnKey}-${stableId}`
  }

  if (bet?.chat_id) {
    return `${columnKey}-${bet.chat_id}-${Number(bet?.sum || 0)}-${Number(bet?.crystal || 0)}`
  }

  return `${columnKey}-fallback-${index}-${String(bet?.name || 'player')}`
}

const DoubleBetsFeed = ({ bets = [], roundStatus, winColor }) => {
  // Затемнение колонок: при кручении все затемнены, при результате — только проигравшие
  const isSpinning = roundStatus === 'raffle'
  const isResult = ['result', 'finished'].includes(roundStatus)

  const getColumnDimmed = (colorKey) => {
    if (isSpinning) return true
    if (isResult && winColor && winColor !== colorKey) return true
    return false
  }
  const { t } = useTranslation()

  const colorColumns = [
    {
      key: 'red',
      cardClass: 'border-red-400/35 bg-red-500/12'
    },
    {
      key: 'black',
      cardClass: 'border-white/20 bg-slate-900/45'
    },
    {
      key: 'green',
      cardClass: 'border-emerald-400/35 bg-emerald-500/12'
    }
  ]

  const safeBets = Array.isArray(bets) ? bets : []

  return (
    <div>
      <div className="grid gap-3 lg:grid-cols-3">
        {colorColumns.map((column) => {
          const columnBets = safeBets
            .filter((bet) => String(bet.color || '') === column.key)
            .sort((a, b) => Number(b?.sum || 0) - Number(a?.sum || 0))

          const isColumnWinner = isResult && winColor === column.key
          const isColumnLoser = isResult && winColor && winColor !== column.key
          const multiplier = COLOR_MULTIPLIER[column.key] || 2

          return (
            <div key={column.key} className={`min-h-0 transition-opacity duration-500 ${getColumnDimmed(column.key) ? 'opacity-30' : 'opacity-100'}`}>
              <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
                <AnimatePresence initial={false}>
                  {columnBets.map((bet, index) => {
                    const betItems = Array.isArray(bet.bet_items) ? bet.bet_items : []
                    const betSum = Number(bet.sum || 0)
                    const isMyBet = Boolean(bet.your_bet)
                    const winSkinsRaw = Array.isArray(bet.win_skin)
                      ? bet.win_skin
                      : (bet.win_skin ? [bet.win_skin] : [])
                    const displayedSkins = isColumnWinner && winSkinsRaw.length > 0 ? winSkinsRaw : betItems
                    const hasDisplayedSkins = displayedSkins.length > 0

                    return (
                      <motion.div
                        key={getBetKey(bet, column.key, index)}
                        layout
                        initial={{ opacity: 0, y: -26, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 22, scale: 0.96 }}
                        transition={{
                          duration: 0.38,
                          ease: [0.22, 1, 0.36, 1],
                          delay: Math.min(index, 6) * 0.035
                        }}
                        className={`rounded-xl border p-3 ${column.cardClass} ${isMyBet ? '!bg-accent-primary/15 !border-accent-primary/45 ring-1 ring-accent-primary/35' : ''}`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <img
                              src={bet.ava}
                              alt={bet.name}
                              className="w-9 h-9 rounded-full object-cover border border-white/20"
                            />
                            <div className="min-w-0">
                              <p className="text-sm text-white font-semibold truncate">{bet.name || t('double.common.player', 'Игрок')}</p>
                            </div>
                          </div>

                          <span className={`inline-flex items-center gap-1 text-sm font-semibold ${isColumnWinner ? 'text-emerald-400' : isColumnLoser ? 'text-red-400' : 'text-accent-primary'}`}>
                            {isColumnWinner ? (
                              <AnimatedWinSum value={betSum} multiplier={multiplier} isAnimating={isResult} />
                            ) : isColumnLoser ? (
                              <AnimatedSum value={betSum} isWinner={false} isLoser={true} isAnimating={isResult} />
                            ) : (
                              betSum.toLocaleString()
                            )}
                            {' '}<Gem className="w-4 h-4" />
                          </span>
                        </div>

                        {hasDisplayedSkins ? (
                          <div className="mt-3">
                            <div className="grid grid-cols-3 gap-2">
                              {displayedSkins.map((item, itemIndex) => (
                                <div
                                  key={`${item.id || itemIndex}-${bet.chat_id || 'anon'}`}
                                  className="rounded-md border border-white/10 bg-dark-primary/60 p-1.5 flex flex-col items-center justify-center text-center"
                                >
                                  <img
                                    src={item.img}
                                    alt={item.name}
                                    className="w-full h-12 object-contain mx-auto"
                                    loading="lazy"
                                  />
                                  <p className="mt-1 text-[10px] text-accent-primary inline-flex items-center justify-center gap-0.5 w-full">
                                    {Number(item.cost ?? item.price ?? 0).toLocaleString()} <Gem className="w-3 h-3" />
                                  </p>
                                </div>
                              ))}

                              {hasDisplayedSkins && Number(bet.crystal || 0) > 0 && (
                                <div className="rounded-md border border-cyan-300/30 bg-cyan-500/10 p-1.5 flex flex-col items-center justify-center text-center">
                                  <div className="w-full h-12 flex items-center justify-center">
                                    <Gem className="w-6 h-6 text-cyan-200" />
                                  </div>
                                  <p className="mt-1 text-[10px] text-cyan-100 inline-flex items-center justify-center gap-0.5 w-full">
                                    {Number(bet.crystal || 0).toLocaleString()} <Gem className="w-3 h-3" />
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : null}
                      </motion.div>
                    )
                  })}
                </AnimatePresence>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default DoubleBetsFeed
